import type { FC, ReactNode } from "react";
import { View } from "@visioni/a3s-ui/components";
import "@visioni/a3s-ui/theme";
import { StyleSheet } from "react-native-unistyles";
import {
  NotificationPreferenceGroup,
  NO_UNAVAILABLE_CHANNELS,
  type NotificationCategory,
  type NotificationCategoryPreferences,
  type NotificationChannel,
} from "../NotificationPreferenceGroup/NotificationPreferenceGroup";
import { NotificationScopeListGroup } from "../NotificationScopeListGroup/NotificationScopeListGroup";
import { NotificationContactPhoneField } from "../NotificationContactPhoneField/NotificationContactPhoneField";
import {
  NotificationPushSwitch,
  type NotificationPushSwitchProps,
} from "../NotificationPushSwitch/NotificationPushSwitch";

/**
 * A channel with nowhere to deliver to has its checkboxes disabled — a
 * checked box with nothing behind it promises a message that never arrives.
 *
 * SMS waits for a phone number on file. This tracks the *saved* number, not
 * what is currently typed into the field: the checkboxes unlock when the
 * number is actually stored, which is what the field's own status line
 * reports.
 *
 * Push waits for somewhere to deliver to. With a push switch, that is this
 * device's registration: another device of the same user may well be
 * registered, but that screen can only vouch for this one. Without one, the
 * caller says through `pushAvailability` (e.g. web, from whether any of the
 * user's devices is registered). Either way, a status still loading is
 * unknown rather than off, so Push stays enabled instead of flickering to
 * disabled on every visit.
 */
function unavailableChannelsFor(options: {
  phoneNumber: string | null;
  pushSwitch: NotificationPushSwitchProps | undefined;
  pushAvailability: PushAvailability;
}): ReadonlySet<NotificationChannel> {
  const unavailable = new Set<NotificationChannel>();
  if ((options.phoneNumber ?? "") === "") unavailable.add("sms");
  const pushSwitch = options.pushSwitch;
  const isPushUnavailable =
    pushSwitch !== undefined ?
      !pushSwitch.isLoading && !pushSwitch.isEnabled
    : options.pushAvailability === "unavailable";
  if (isPushUnavailable) unavailable.add("push");
  return unavailable.size === 0 ? NO_UNAVAILABLE_CHANNELS : unavailable;
}

/** Whether push can currently be delivered anywhere — `"unknown"` while that is still being found out. */
export type PushAvailability = "available" | "unavailable" | "unknown";

export interface NotificationPreferencesFormSite {
  readonly siteKey: string;
  readonly label: string;
  readonly preferences: NotificationCategoryPreferences;
  /** See `NotificationPreferenceGroupProps.inherited` — typically the "All Cameras" preferences. */
  readonly inherited?: NotificationCategoryPreferences | undefined;
}

export interface CameraNotificationPreference {
  readonly cameraKey: string;
  readonly label: string;
  readonly preferences: NotificationCategoryPreferences;
  /** See `NotificationPreferenceGroupProps.inherited` — typically "All Cameras" merged with this camera's own site. */
  readonly inherited?: NotificationCategoryPreferences | undefined;
}

export interface NotificationPreferencesFormProps {
  readonly allCameras: NotificationCategoryPreferences;
  readonly onToggleAllCamerasChannel: (
    category: NotificationCategory,
    channel: NotificationChannel,
    enabled: boolean,
  ) => void;
  readonly sites: readonly NotificationPreferencesFormSite[];
  readonly onToggleSiteChannel: (
    siteKey: string,
    category: NotificationCategory,
    channel: NotificationChannel,
    enabled: boolean,
  ) => void;
  readonly cameras: readonly CameraNotificationPreference[];
  readonly onToggleCameraChannel: (
    cameraKey: string,
    category: NotificationCategory,
    channel: NotificationChannel,
    enabled: boolean,
  ) => void;
  /** `null` if the user has never set one — see `NotificationContactPhoneField`. */
  readonly phoneNumber: string | null;
  readonly onSavePhoneNumber: (phoneNumber: string | null) => void;
  /**
   * This device's push registration, rendered as a switch at the top of the
   * form. While it is off, the Push checkboxes are disabled at whatever they
   * are set to. Omit where this device cannot register (web), and say whether
   * push is available through `pushAvailability` instead.
   */
  readonly pushSwitch?: NotificationPushSwitchProps | undefined;
  /**
   * Whether push can be delivered, for a form with no `pushSwitch`: Push
   * checkboxes are disabled when `"unavailable"`. Defaults to `"available"`.
   * Ignored when `pushSwitch` is given, which decides it instead.
   */
  readonly pushAvailability?: PushAvailability | undefined;
  /** Rendered at the top of the form — e.g. why Push is unavailable and how to fix it. */
  readonly pushNotice?: ReactNode;
  /** See `NotificationPreferenceGroupProps.hiddenChannels` — applied to every scope in the form. */
  readonly hiddenChannels?: ReadonlySet<NotificationChannel> | undefined;
  readonly testID?: string;
}

/**
 * An individual user's own notification settings — self-service, account-holders
 * only (no arbitrary contacts here, unlike the tenant-wide Contacts feature).
 *
 * Three independent scopes, each the same "Email/SMS/Push × Alarm Events/Alarm
 * Setting Changes" shape: a global "All Cameras" default, a "Sites" section
 * listing every site the user has access to, and a "Cameras" section listing
 * every camera — both list sections share `NotificationScopeListGroup`. Each is
 * its own set of toggles; at dispatch the scopes combine by OR (a narrower scope
 * can add channels but not remove ones a broader scope grants), which the caller
 * shows by passing a broader scope's channels as `inherited`.
 */
export const NotificationPreferencesForm: FC<NotificationPreferencesFormProps> = ({
  allCameras,
  onToggleAllCamerasChannel,
  sites,
  onToggleSiteChannel,
  cameras,
  onToggleCameraChannel,
  phoneNumber,
  onSavePhoneNumber,
  pushSwitch,
  pushAvailability = "available",
  pushNotice,
  hiddenChannels = NO_UNAVAILABLE_CHANNELS,
  testID,
}) => {
  const unavailableChannels = unavailableChannelsFor({ phoneNumber, pushSwitch, pushAvailability });

  return (
    <View style={styles.container} testID={testID}>
      {pushNotice}
      {pushSwitch !== undefined && (
        <>
          <NotificationPushSwitch {...pushSwitch} />
          <View style={styles.divider} />
        </>
      )}
      <NotificationContactPhoneField phoneNumber={phoneNumber} onSave={onSavePhoneNumber} />
      <View style={styles.divider} />
      <NotificationPreferenceGroup
        label="All Cameras"
        preferences={allCameras}
        onToggleChannel={onToggleAllCamerasChannel}
        unavailableChannels={unavailableChannels}
        hiddenChannels={hiddenChannels}
      />
      <View style={styles.divider} />
      <NotificationScopeListGroup
        label="Sites"
        items={sites.map((site) => ({
          key: site.siteKey,
          label: site.label,
          preferences: site.preferences,
          inherited: site.inherited,
        }))}
        onToggleChannel={onToggleSiteChannel}
        unavailableChannels={unavailableChannels}
        hiddenChannels={hiddenChannels}
      />
      <View style={styles.divider} />
      <NotificationScopeListGroup
        label="Cameras"
        items={cameras.map((camera) => ({
          key: camera.cameraKey,
          label: camera.label,
          preferences: camera.preferences,
          inherited: camera.inherited,
        }))}
        onToggleChannel={onToggleCameraChannel}
        unavailableChannels={unavailableChannels}
        hiddenChannels={hiddenChannels}
      />
    </View>
  );
};

const styles = StyleSheet.create((theme) => ({
  container: {
    gap: theme.spacing.lg,
  },
  divider: {
    height: 1,
    backgroundColor: theme.colors.border,
  },
}));
